import { useEffect, useState } from "react";

import { API_SQE_VERIFIED_EMPLOYEES } from "../../config/Endpoints.js";
import type { SelectOption } from "../components/ui/select/select.tsx";
import { getApi } from "../utils/api.ts";
import { accessToken } from "../utils/auth.ts";

let cachedToken: string | undefined | null = null;
let cachedOptions: SelectOption[] | null = null;
let pendingRequest: Promise<SelectOption[]> | null = null;

function parseOptions(payload: unknown): SelectOption[] {
  const data = payload && typeof payload === "object" && "data" in payload
    ? (payload as { data?: unknown }).data
    : payload;
  if (!Array.isArray(data)) return [];

  return data.flatMap((row) => {
    if (!row || typeof row !== "object") return [];
    const employee = row as Record<string, unknown>;
    const id = employee.user_id;
    const name = employee.employeeName;
    return id != null && typeof name === "string" && name.trim()
      ? [{ value: String(id), label: name.trim() }]
      : [];
  });
}

function loadOptions(): Promise<SelectOption[]> {
  const token = accessToken();
  if (cachedToken !== token) {
    cachedToken = token;
    cachedOptions = null;
    pendingRequest = null;
  }
  if (cachedOptions) return Promise.resolve(cachedOptions);
  if (!pendingRequest) {
    pendingRequest = getApi<unknown>(API_SQE_VERIFIED_EMPLOYEES, token)
      .then(({ data }) => {
        const options = parseOptions(data);
        if (cachedToken === token) cachedOptions = options;
        return options;
      })
      .finally(() => {
        if (cachedToken === token) pendingRequest = null;
      });
  }
  return pendingRequest;
}

export function useSqeEmployeeNameOptions(enabled = true) {
  const [options, setOptions] = useState<SelectOption[]>(cachedOptions ?? []);
  const [loading, setLoading] = useState(enabled && !cachedOptions);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setLoading(true);
    setError(null);
    void loadOptions().then((nextOptions) => {
      if (active) setOptions(nextOptions);
    }).catch(() => {
      if (active) {
        setOptions([]);
        setError("Unable to load SQE employees.");
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [enabled]);

  return { options, loading, error };
}
